/**
 * Client typé pour les routes `/photos` — Phase 4, incrément 4.9,
 * étendu **offline-first** en Phase 3.3.3 (mutations PATCH/DELETE)
 * puis **3.3.6** (upload binaire 3-step queue-able).
 *
 * Flux d'upload en 3 étapes (cf. ADR 0010) :
 *
 *   1. `requestUploadUrl(homeId, { mime, byteSize })` → presigned
 *      POST + photoId pré-réservé.
 *   2. `uploadBlobToR2(uploadUrl, uploadFields, blob)` → upload
 *      direct vers R2 (le serveur ne proxie pas les bytes).
 *   3. `confirmPhoto(homeId, { photoId, mediaKey, ... })` → insert
 *      la row + retourne le Photo complet.
 *
 * Le helper `uploadPhotoBlob` orchestre les 3 étapes en une seule
 * fonction async. **3.3.6** : il devient **offline-first** comme
 * `uploadVoicemail` (3.3.5). Si une étape réseau échoue, le blob
 * est persisté en IDB et une media-mutation `confirmKind: 'photo'`
 * est enqueued. Le replay ré-allouera un presign frais et
 * complétera l'upload à la reconnexion.
 *
 * Mutations PATCH/DELETE — refactorées en 3.3.3 vers
 * `sendOrQueueMutation` : retournent `SendResult<T>`.
 */
import { apiRequest } from './client.js';
import {
  isNetworkError,
  isOffline,
  sendOrQueueMutation,
  type SendResult,
} from './mutationsReplay.js';
import { enqueueMediaMutation } from '../storage/mutationsQueue.js';
import { deleteBlob, putBlob } from '../storage/blobs.js';
import { bumpPendingCount } from '../stores/mutationsQueueStore.js';
import { showToast } from '../ui/Toast.js';
import { t } from '../i18n/index.js';

export interface ApiPhoto {
  id: string;
  homeId: string;
  mediaKey: string;
  mime: string;
  byteSize: number;
  caption: string | null;
  albumName: string | null;
  takenAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Presigned GET URL valide 15 min. `null` si R2 down. */
  url: string | null;
}

export type PhotoMime = 'image/jpeg' | 'image/png' | 'image/webp';

export interface UploadUrlInput {
  mime: PhotoMime;
  byteSize: number;
}

export interface UploadUrlResponse {
  photoId: string;
  mediaKey: string;
  uploadUrl: string;
  uploadFields: Record<string, string>;
  expiresIn: number;
}

export interface ConfirmPhotoInput {
  photoId: string;
  mediaKey: string;
  mime: PhotoMime;
  byteSize: number;
  caption?: string;
  albumName?: string;
  takenAt?: string; // ISO 8601
}

export interface UpdatePhotoInput {
  caption?: string | null;
  albumName?: string | null;
}

export interface UploadPhotoMetadata {
  caption?: string;
  albumName?: string;
  /** ISO 8601 datetime. */
  takenAt?: string;
}

/**
 * Étape 1 : demande au serveur une presigned URL R2 + un photoId
 * pré-réservé. Throw `ApiError` 400 si mime non autorisé ou taille
 * > 5 Mo, 503 si R2 down, 403 si pas membre.
 */
export async function requestUploadUrl(
  homeId: string,
  input: UploadUrlInput,
): Promise<UploadUrlResponse> {
  return apiRequest<UploadUrlResponse>(`/homes/${homeId}/photos/upload-url`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Étape 2 : upload direct vers R2 via le presigned POST. Pas de
 * cookies envoyés (R2 ne les comprend pas), pas de wrapping
 * `apiRequest` — c'est un POST multipart standard.
 *
 * Throw `Error` si R2 rejette (statut >= 400). Le caller doit
 * gérer ; l'app ne crée pas la row tant que cet upload n'est
 * pas confirmé.
 */
export async function uploadBlobToR2(
  uploadUrl: string,
  uploadFields: Record<string, string>,
  blob: Blob,
): Promise<void> {
  const formData = new FormData();
  for (const [k, v] of Object.entries(uploadFields)) {
    formData.append(k, v);
  }
  // R2 attend le fichier en dernier dans le multipart (convention S3).
  formData.append('file', blob);

  const res = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
    // Pas de credentials: 'include' — R2 est cross-origin pur,
    // le POST signé ne nécessite que les champs de policy.
  });
  if (!res.ok) {
    throw new Error(`R2 upload failed: ${res.status.toString()} ${res.statusText}`);
  }
}

/**
 * Étape 3 : confirme l'upload côté API. Le serveur HEAD R2 pour
 * vérifier la présence, insère la row, retourne le Photo complet
 * (avec URL signée GET valide 15 min).
 *
 * Throw `ApiError` 400 si HEAD échoue (upload pas fait), 503 si
 * R2 down, 403 si pas membre.
 */
export async function confirmPhoto(homeId: string, input: ConfirmPhotoInput): Promise<ApiPhoto> {
  const data = await apiRequest<{ photo: ApiPhoto }>(`/homes/${homeId}/photos`, {
    method: 'POST',
    body: input,
  });
  return data.photo;
}

/**
 * Orchestrate les 3 étapes en une seule fonction — **offline-first
 * en 3.3.6**.
 *
 * Pattern symétrique à `uploadVoicemail` (3.3.5) :
 *
 *   1. Persiste TOUJOURS le blob IDB d'abord sous
 *      `photo:<localId>` (UUID v4 client). Le blob survit à un
 *      crash entre les étapes.
 *   2. Si en ligne, tente le flow 3-step direct. Si tout réussit :
 *      retourne `{kind: 'sent', data: {photo}}` + purge le blob
 *      (R2 + DB serveur en ont la copie).
 *   3. Si offline ou si une étape réseau échoue (presign, R2 PUT,
 *      confirm) : `enqueueMediaMutation` avec `confirmKind:
 *      'photo'` + extras (caption, albumName, takenAt) ; retourne
 *      `{kind: 'queued', seq}`. Le replay ré-allouera un presign
 *      frais à la reconnexion.
 *   4. Si erreur serveur (ApiError) : purge le blob + rethrow.
 */
export async function uploadPhotoBlob(
  homeId: string,
  blob: Blob,
  metadata: UploadPhotoMetadata = {},
  /** Phase 3.3.7 — id local du store domain pour le remap au replay. */
  storeLocalId?: string,
): Promise<SendResult<{ photo: ApiPhoto }>> {
  const mime = (blob.type || 'image/jpeg') as PhotoMime;
  const byteSize = blob.size;

  // 1. Persiste le blob en IDB AVANT toute tentative réseau.
  const internalLocalId = generateLocalId();
  const blobKey = `photo:${internalLocalId}`;
  await putBlob(blobKey, blob);

  // 2. Tentative en ligne — flow 3-step direct si tout réussit.
  if (!isOffline()) {
    try {
      const presigned = await requestUploadUrl(homeId, { mime, byteSize });
      await uploadBlobToR2(presigned.uploadUrl, presigned.uploadFields, blob);
      const photo = await confirmPhoto(homeId, {
        photoId: presigned.photoId,
        mediaKey: presigned.mediaKey,
        mime,
        byteSize,
        ...metadata,
      });
      // R2 + serveur ont la copie — libère IDB local.
      await deleteBlob(blobKey);
      // Phase 3.3.7 : remap localId → serverId pour le store
      // domain (évite le doublon visuel au prochain GET refresh).
      if (storeLocalId && typeof window !== 'undefined') {
        // Lazy import pour ne pas créer un cycle.
        import('./localIdRemap.js')
          .then(({ emitLocalIdRemap }) => {
            emitLocalIdRemap({
              path: `/homes/${homeId}/photos`,
              localId: storeLocalId,
              serverId: photo.id,
            });
          })
          .catch(() => undefined);
      }
      return { kind: 'sent', data: { photo } };
    } catch (err) {
      if (!isNetworkError(err)) {
        // Server error (ApiError) — pas d'enqueue, rethrow. Purge
        // aussi le blob (serveur a rejeté : taille / mime invalide,
        // 403, etc. — garder le blob n'a pas de sens).
        await deleteBlob(blobKey);
        throw err;
      }
      // Network err — fall-through vers l'enqueue ci-dessous.
    }
  }

  // 3. Offline ou network failure → enqueue media-mutation.
  const seq = await enqueueMediaMutation(
    {
      blobKey,
      mime,
      byteSize,
      uploadUrlPath: `/homes/${homeId}/photos/upload-url`,
      confirmPath: `/homes/${homeId}/photos`,
      confirmKind: 'photo',
      confirmExtras: buildPhotoConfirmExtras(metadata),
    },
    storeLocalId,
  );
  notifyPhotoQueued();
  return { kind: 'queued', seq };
}

/**
 * UUID v4 généré côté client. Utilise `crypto.randomUUID` si dispo,
 * fallback pseudo-aléatoire sinon. Identique au helper de
 * `messages.ts` — pas mutualisé pour éviter un module utility de
 * plus (les deux callers sont indépendants).
 */
function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Filtre les `undefined` du metadata pour ne stocker que les
 * champs réellement passés. Cohérent avec le schéma zod côté
 * serveur (`confirmSchema` accepte les champs `.optional()`).
 */
function buildPhotoConfirmExtras(meta: UploadPhotoMetadata): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (meta.caption !== undefined) out['caption'] = meta.caption;
  if (meta.albumName !== undefined) out['albumName'] = meta.albumName;
  if (meta.takenAt !== undefined) out['takenAt'] = meta.takenAt;
  return out;
}

/**
 * Effet de bord UI à l'enqueue d'une photo — bump le store + toast
 * i18n. Identique à `notifyVoicemailQueued` dans `messages.ts`.
 * Try/catch défensif.
 */
function notifyPhotoQueued(): void {
  try {
    bumpPendingCount();
    showToast({ message: t('mutations_queued'), kind: 'info' });
  } catch (err) {
    console.warn('[photos] notifyPhotoQueued side-effect failed:', err);
  }
}

export async function listPhotos(homeId: string): Promise<ApiPhoto[]> {
  const data = await apiRequest<{ photos: ApiPhoto[] }>(`/homes/${homeId}/photos`);
  return data.photos;
}

/**
 * PATCH `/photos/:id` — caption / albumName. Offline-first via
 * `sendOrQueueMutation` (pattern 3.3.3).
 */
export async function updatePhoto(
  id: string,
  input: UpdatePhotoInput,
): Promise<SendResult<{ photo: ApiPhoto }>> {
  return sendOrQueueMutation<{ photo: ApiPhoto }>({
    method: 'PATCH',
    path: `/photos/${id}`,
    body: input,
  });
}

/**
 * DELETE `/photos/:id`. Offline-first via `sendOrQueueMutation`.
 */
export async function deletePhoto(id: string): Promise<SendResult<void>> {
  return sendOrQueueMutation<void>({
    method: 'DELETE',
    path: `/photos/${id}`,
  });
}
